import React, { forwardRef, useCallback, useId, useMemo } from "react";
import Card from "./Card";
import Icon from "./Icon";
import StatePanel from "./StatePanel";
import useBreakpoint from "../../hooks/useBreakpoint";
import "./DataTable.scss";

// Fontaine M36 — DataTable (`ft-table`). ONE tabular surface with TWO presentations of the same
// data, chosen by width, from ONE set of column definitions:
//
//   >= 900px  a real <table>: 36px compact rows (charter — `--row-h-compact`), an optional sticky
//             header, `aria-sort` on sortable headers.
//   <  900px  one Card per row: a card list built from the SAME `cell` renderers, regrouped by
//             each column's `card` role. No horizontal scrollbar — the charter's "phone never
//             shows a visible scrollbar" rule is met by not overflowing at all, not by hiding one.
//
// THE CONTRACT THAT MATTERS, in one place so a reviewer need not read the CSS:
//   · Exactly ONE presentation mounts at a time — only the active one ever calls a cell renderer,
//     so a row costs one presentation's worth of work at either width, not both. That is why the
//     switch is `useBreakpoint` and not a pair of `display: none` blocks. (React still re-renders
//     the chosen presentation as it pleases; a `cell` must stay a pure render function.)
//   · `cell(row, meta)` is called with identical arguments in both presentations. Whatever a
//     caller returns — a <Link>, a <Button>, a <Toggle>, a disabled control, a menu — is placed
//     verbatim. DataTable never clones, wraps, re-binds or filters a cell's nodes, so handlers,
//     hrefs, `disabled`, `aria-*` and analytics props survive the switch untouched.
//   · The card presentation drops NO column. A column with no explicit `card` role becomes a
//     labelled meta pair; only `card: "hidden"` removes it, and only where the caller says so.
//   · Sorting is CONTROLLED and presentation-independent: DataTable sorts nothing itself. It
//     reports `onSortChange(field, direction)` and renders the order it is handed, so the
//     server-side sort every live table already uses keeps working.
//   · Row activation is a real <button> in both presentations — focusable, named, Enter/Space
//     from the browser — never a bare `onClick` on a <tr>, and never an interactive ARIA role
//     forced onto a table row. A nested link, button or toggle activates itself and ONLY itself.
//   · The application states are StatePanel's (M37), not new ones — one state vocabulary.
//
// There is no frozen Style Guide V2 table specimen (checked: the guide has card, listing-card and
// pagination specimens, no data row). So the visual authority here is the charter's locked line
// "Tables use 36px compact rows on laptop and one card per row on phone. Phone never shows a
// visible scrollbar." plus the existing tokens — no new colour, radius or type value is invented.

const ALIGNS = ["start", "center", "end"];

/**
 * Anything inside a row that the browser (or ARIA) already treats as its own activation target.
 * A pointer click that STARTED on one of these belongs to it alone — a <Link>, a <Button>, a
 * <Toggle> inside a row must never also open the row. See `startedOnAControl`.
 */
const INTERACTIVE_IN_ROW = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  "summary",
  "label",
  '[role="button"]',
  '[role="link"]',
  '[role="switch"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="tab"]',
  '[role="option"]',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/** True when this pointer event began on an interactive descendant of the row it bubbled to. */
const startedOnAControl = (event) => {
  const { target, currentTarget } = event;
  if (!target || typeof target.closest !== "function") return false;
  const control = target.closest(INTERACTIVE_IN_ROW);
  // `closest` walks past the row too, so an interactive ANCESTOR of the table must not count.
  return !!control && control !== currentTarget && currentTarget.contains(control);
};

/**
 * Where a column goes when the row becomes a card.
 *   title      the card's headline (the first title column carries the row link, if any)
 *   subtitle   the line under the headline
 *   aside      top-right of the card — a status, a badge, a figure
 *   meta       a labelled label/value pair in the card's body  (DEFAULT)
 *   actions    the card's action band, at the bottom
 *   hidden     table only — the caller's explicit choice to drop it from the card
 */
const CARD_ROLES = ["title", "subtitle", "aside", "meta", "actions", "hidden"];
const STATES = ["loading", "error", "empty", "no-results"];

const roleOf = (column) => (CARD_ROLES.includes(column.card) ? column.card : "meta");
const alignOf = (column) => (ALIGNS.includes(column.align) ? column.align : "start");

/** Columns are objects; a bare string is shorthand for `{ key: s, header: s }`. */
const normaliseColumns = (columns) =>
  (Array.isArray(columns) ? columns : [])
    .filter(Boolean)
    .map((column, index) => {
      const base = typeof column === "string" ? { key: column, header: column } : column;
      return { ...base, key: base.key ?? `col-${index}` };
    });

/**
 * One cell's content. `cell(row, meta)` wins; otherwise the row's own `key` field is read.
 *
 * A missing value becomes an em dash, so a data cell is never silently blank — except in an
 * `actions` column, where "no action for this row" is a real answer and a dash would be noise.
 * `emptyValue` overrides either default (pass `null` for nothing at all).
 */
const renderCell = (column, row, meta) => {
  const value = typeof column.cell === "function" ? column.cell(row, meta) : row?.[column.key];
  if (value !== null && value !== undefined && value !== "") return value;
  if ("emptyValue" in column) return column.emptyValue;
  return roleOf(column) === "actions" ? null : "—";
};

/**
 * The accessible name of a row's open control. A string names every row the same way, which is
 * fine inside a table or a list where the row itself supplies the context; a function lets a
 * caller name each one individually ("Open 115234-0002") where that reads better.
 */
const labelOf = (rowActionLabel, row, index) =>
  typeof rowActionLabel === "function" ? rowActionLabel(row, index) : rowActionLabel;

/**
 * The row-activation control, shared by BOTH presentations. It is a real <button>, so the browser
 * gives it a tab stop, a focus ring, an accessible name and Enter/Space activation for free — no
 * role or key handler is synthesised anywhere, and no interactive role is put on a <tr>.
 */
function RowOpenControl({ className, label, onActivate }) {
  return (
    <button
      type="button"
      className={className}
      onClick={(event) => {
        // The row itself may carry a pointer affordance; this click has already been answered.
        event.stopPropagation();
        onActivate(event);
      }}
    >
      <span className="ft-table__sr">{label}</span>
      <Icon name="ChevronRight" size="sm" aria-hidden="true" />
    </button>
  );
}

/** The row's key: the caller's `rowKey`, else a stable `id`, else the index (last resort). */
const keyOf = (rowKey, row, index) => {
  if (typeof rowKey === "function") return rowKey(row, index);
  if (typeof rowKey === "string" && row?.[rowKey] != null) return row[rowKey];
  if (row?.id != null) return row.id;
  return index;
};

/** A sortable header is a real <button> inside the <th>, so Enter / Space come from the browser. */
function HeaderCell({ column, sort, onSortChange }) {
  const field = column.sortKey ?? column.key;
  const sortable = !!column.sortable && typeof onSortChange === "function";
  const isSorted = sortable && !!sort && sort.field === field;
  const direction = isSorted ? sort.direction : null;
  const label = column.header ?? "";

  const classes = ["ft-table__th", `ft-table__th--${alignOf(column)}`, sortable ? "ft-table__th--sortable" : "", column.className || ""]
    .filter(Boolean)
    .join(" ");

  const labelNode = column.headerHidden ? <span className="ft-table__sr">{label}</span> : label;

  return (
    <th
      scope="col"
      className={classes}
      // No `width` prop by design: an inline style is barred by the lint fence, and a table whose
      // columns carry arbitrary pixel widths drifts off the token scale. Size a column from the
      // page's own stylesheet via its `className`, which is what the live tables already do.
      // aria-sort belongs on the header CELL, not on the button inside it, and only where sorting
      // is actually wired: "none" on a sortable-but-unsorted column, absent on a plain one.
      aria-sort={isSorted ? (direction === "desc" ? "descending" : "ascending") : sortable ? "none" : undefined}
      data-column={column.key}
    >
      {sortable ? (
        <button
          type="button"
          className="ft-table__sort"
          onClick={() =>
            onSortChange(field, isSorted ? (direction === "asc" ? "desc" : "asc") : column.defaultDirection || "asc")
          }
        >
          <span className="ft-table__sort-label">{labelNode}</span>
          <Icon
            name={isSorted ? (direction === "desc" ? "ArrowDown" : "ArrowUp") : "ChevronsUpDown"}
            size="sm"
            className="ft-table__sort-icon"
            aria-hidden="true"
          />
        </button>
      ) : (
        labelNode
      )}
    </th>
  );
}

/**
 * ONE row as a card. Built from the same columns and the same `cell` calls the table uses — only
 * grouped differently. Whatever a cell returns is placed verbatim.
 */
function RowCard({
  columns,
  row,
  index,
  rowKey,
  meta,
  href,
  to,
  onRowClick,
  rowActionLabel,
  LinkComponent,
  className = "",
  ...rest
}) {
  const grouped = useMemo(() => {
    const buckets = { title: [], subtitle: [], aside: [], meta: [], actions: [] };
    for (const column of columns) {
      if (column.cardHidden) continue;
      const role = roleOf(column);
      if (role === "hidden") continue;
      buckets[role].push(column);
    }
    return buckets;
  }, [columns]);

  // The headline is a link only when the caller gave the row one. `to` goes through the caller's
  // own Link component — react-router stays out of this primitive; `href` is a plain anchor.
  const wrapTitle = (node, column) => {
    if (to != null && LinkComponent) {
      return (
        <LinkComponent to={to} className="ft-table__card-title-link" {...(column.linkProps || {})}>
          {node}
        </LinkComponent>
      );
    }
    if (href != null) {
      return (
        <a href={href} className="ft-table__card-title-link" {...(column.linkProps || {})}>
          {node}
        </a>
      );
    }
    return node;
  };

  const hasHead = grouped.title.length || grouped.subtitle.length || grouped.aside.length;
  const openable = typeof onRowClick === "function";

  return (
    <Card
      as="li"
      // `sm` IS `var(--space-3)` on Card's own padding scale. Asking Card for the padding rather
      // than setting `none` here and re-padding in DataTable.scss avoids a source-order race:
      // `.ft-card--pad-none` and `.ft-table__card` are both single-class selectors, so whichever
      // stylesheet loaded last would win.
      padding="sm"
      // `--openable` is what reserves the inline-end gutter the pinned control sits in. It is on
      // the CARD, not on `__card-aside`, because the reservation is needed whenever the control
      // exists — a card with a long title and no aside column needs it just as much.
      className={["ft-table__card", openable ? "ft-table__card--openable" : "", className]
        .filter(Boolean)
        .join(" ")}
      data-row-key={String(keyOf(rowKey, row, index))}
      {...rest}
    >
      {hasHead ? (
        <div className="ft-table__card-head">
          <div className="ft-table__card-headings">
            {grouped.title.map((column, i) => (
              <span className="ft-table__card-title" key={column.key} data-column={column.key}>
                {i === 0
                  ? wrapTitle(renderCell(column, row, meta), column)
                  : renderCell(column, row, meta)}
              </span>
            ))}
            {grouped.subtitle.map((column) => (
              <span className="ft-table__card-subtitle" key={column.key} data-column={column.key}>
                {renderCell(column, row, meta)}
              </span>
            ))}
          </div>
          {grouped.aside.length ? (
            <div className="ft-table__card-aside">
              {grouped.aside.map((column) => (
                <span key={column.key} data-column={column.key}>
                  {renderCell(column, row, meta)}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {grouped.meta.length ? (
        // A <dl>, not a grid of divs: each value keeps its column header as its programmatic
        // label, so a screen reader gets the same "Status / Reserved" pairing the <th> gave it.
        <dl className="ft-table__card-meta">
          {grouped.meta.map((column) => (
            <div className="ft-table__card-pair" key={column.key} data-column={column.key}>
              <dt className="ft-table__card-label">{column.cardLabel ?? column.header}</dt>
              <dd className={`ft-table__card-value ft-table__card-value--${alignOf(column)}`}>
                {renderCell(column, row, meta)}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}

      {grouped.actions.length ? (
        <div className="ft-table__card-actions">
          {grouped.actions.map((column) => (
            <span key={column.key} className="ft-table__card-action" data-column={column.key}>
              {renderCell(column, row, meta)}
            </span>
          ))}
        </div>
      ) : null}

      {/* A whole-row activation target, when the caller wants one. It is a sibling control rather
          than a wrapper around the card, so the buttons, toggles and links inside the card stay
          independently reachable instead of being nested inside another activatable box. */}
      {openable ? (
        <RowOpenControl
          className="ft-table__card-open"
          label={labelOf(rowActionLabel, row, index)}
          onActivate={(event) => onRowClick(row, index, event)}
        />
      ) : null}
    </Card>
  );
}

/**
 * @param {Array<object|string>} columns  [{ key, header, cell(row, meta), align, sortable,
 *                                        sortKey, defaultDirection, card, cardLabel, cardHidden,
 *                                        tableHidden, headerHidden, className, emptyValue,
 *                                        linkProps }]  — size a column via `className`, not a
 *                                        `width` prop; see HeaderCell
 * @param {Array<object>} rows            the rows, ALREADY in the order to display
 * @param {Function|string} [rowKey]      (row, i) => key, or the name of an id field
 * @param {object} [sort]                 { field, direction: "asc"|"desc" } — controlled
 * @param {Function} [onSortChange]       (field, direction) => void; without it no header sorts
 * @param {Function} [onRowClick]         (row, i, event) => void — row activation. BOTH
 *                                        presentations get a real focusable <button> for it (a
 *                                        trailing cell on laptop, the pinned chevron on the
 *                                        card); on laptop the <tr> also answers a pointer click,
 *                                        but never one that started on a control inside the row
 * @param {Function} [rowHref]            (row, i) => string — plain anchor on the card headline
 * @param {Function} [rowTo]              (row, i) => router target; needs `linkComponent`
 * @param {React.ElementType} [linkComponent]  the caller's Link (react-router stays out of here)
 * @param {Function} [rowProps]           (row, i) => extra props for the <tr> / the card
 * @param {string|Function} [rowActionLabel="Open"]  accessible name of the row-activation control
 *                                        in either presentation; `(row, i) => string` names each
 *                                        row individually. A string also names the laptop
 *                                        presentation's open column for a screen reader.
 * @param {"loading"|"error"|"empty"|"no-results"} [state]  StatePanel takes over the rows
 * @param {object} [stateProps]           forwarded to StatePanel (title, description, action, onRetry…)
 * @param {string} caption                what this table IS — needed for a11y; visually hidden
 *                                        unless `captionVisible`
 * @param {boolean} [captionVisible=false]
 * @param {boolean} [stickyHeader=false]
 * @param {"compact"|"comfortable"} [density="compact"]  36px vs 52px rows (`--row-h-*`)
 * @param {ReactNode} [footer]            below the rows in BOTH presentations (pager, totals)
 * @param {boolean} [forceTable=false]    keep the table at every width — only for a genuine
 *                                        matrix of figures, never to skip designing the phone case
 * @param {boolean} [forceCards=false]    render the CARD list at every width — for a page that
 *                                        offers an explicit grid/list switch. Wins over forceTable.
 * @param {(row, index) => ReactNode} [renderRowDetail]
 *                                        OPT-IN row expansion. Return a node to render it directly
 *                                        beneath that row — a full-width <tr> in the table, a
 *                                        sibling <li> in the card list — and null (the default, and
 *                                        what every existing caller does by omitting this) to render
 *                                        nothing. The caller owns which row is open and the control
 *                                        that toggles it; this only places the result next to the
 *                                        record it describes instead of after the whole group.
 */
const DataTable = forwardRef(function DataTable(
  {
    columns,
    rows = [],
    rowKey,
    sort,
    onSortChange,
    onRowClick,
    rowHref,
    rowTo,
    linkComponent: LinkComponent,
    rowProps,
    rowActionLabel = "Open",
    state,
    stateProps,
    caption,
    captionVisible = false,
    stickyHeader = false,
    density = "compact",
    footer,
    forceTable = false,
    forceCards = false,
    // Opt-in: render the header row visually hidden. For a page that stacks
    // several tables sharing one column set, repeating the header above every
    // one is noise — but it still has to exist, for screen readers and so the
    // cells are associated with something.
    hideHeader = false,
    renderRowDetail,
    className = "",
    ...rest
  },
  ref
) {
  const uid = useId().replace(/:/g, "");
  const { isAtLeastMd } = useBreakpoint();
  // `forceCards` is the symmetric twin of `forceTable`, for a caller offering a
  // deliberate GRID view at laptop width. It wins over `forceTable` because a
  // user's explicit choice should beat a page's default. Below the breakpoint
  // the card list is already what renders, so this changes nothing there.
  const asTable = !forceCards && (forceTable || isAtLeastMd);

  const resolved = useMemo(() => normaliseColumns(columns), [columns]);
  const tableColumns = useMemo(() => resolved.filter((column) => !column.tableHidden), [resolved]);
  const resolvedState = STATES.includes(state) ? state : null;
  const captionId = `${uid}-caption`;

  // Row activation adds a real column to the table — the open control is a focusable <button> in
  // its own cell, not a click handler smuggled onto the <tr>. The <tr> keeps a POINTER affordance
  // on top of it (hover + click), which is a convenience, never the only way in.
  const clickable = typeof onRowClick === "function";
  const openColumnHeader = typeof rowActionLabel === "string" ? rowActionLabel : "Open";
  const columnCount = tableColumns.length + (clickable ? 1 : 0) || 1;

  // Handed to every `cell` call so a caller that genuinely needs to know can ask — without
  // reaching for `useBreakpoint` again and without DataTable deciding anything for it.
  const meta = useMemo(
    () => ({ presentation: asTable ? "table" : "cards", density }),
    [asTable, density]
  );

  const classes = [
    "ft-table",
    asTable ? "ft-table--table" : "ft-table--cards",
    `ft-table--${density === "comfortable" ? "comfortable" : "compact"}`,
    stickyHeader ? "ft-table--sticky" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const extraRowProps = useCallback(
    (row, index) => (typeof rowProps === "function" ? rowProps(row, index) || {} : {}),
    [rowProps]
  );

  // A state panel replaces the ROWS, never the whole component: the caption, the column header
  // and any footer (a pager, a total) stay put, so the page does not jump as data arrives and the
  // reader never loses the column names they were just reading.
  const statePanel = resolvedState ? (
    <div className="ft-table__state" data-state={resolvedState}>
      <StatePanel
        state={resolvedState}
        compact
        loadingShape={asTable ? "row" : "card"}
        loadingCount={4}
        {...stateProps}
      />
    </div>
  ) : null;

  // BOTH presentations are built lazily. As plain `const … = (<jsx/>)` the table branch called
  // `renderCell` for every row even when the card branch was the one being rendered — the cell
  // calls happen while the element tree is CONSTRUCTED, not when React renders it. That silently
  // doubled the work on phone and reported the wrong `meta.presentation` to the discarded pass.
  const cardList = () => (
    <ul className="ft-table__cards" aria-labelledby={caption ? captionId : undefined}>
      {rows.map((row, index) => {
        const detail = renderRowDetail ? renderRowDetail(row, index) : null;
        return (
          <React.Fragment key={keyOf(rowKey, row, index)}>
            <RowCard
              columns={resolved}
              row={row}
              index={index}
              rowKey={rowKey}
              meta={meta}
              href={typeof rowHref === "function" ? rowHref(row, index) : undefined}
              to={typeof rowTo === "function" ? rowTo(row, index) : undefined}
              onRowClick={onRowClick}
              rowActionLabel={rowActionLabel}
              LinkComponent={LinkComponent}
              {...extraRowProps(row, index)}
            />

            {/* The card twin of the detail row. A sibling <li>, not nested
                inside the card, so the card keeps its own padding contract and
                a long panel is not squeezed into a 36px-rhythm layout. */}
            {detail ? (
              <li className="ft-table__card-detail">{detail}</li>
            ) : null}
          </React.Fragment>
        );
      })}
    </ul>
  );

  const tableView = () => (
    <div className="ft-table__scroll">
      <table className="ft-table__table">
        {caption ? (
          <caption className={captionVisible ? "ft-table__caption" : "ft-table__sr"} id={captionId}>
            {caption}
          </caption>
        ) : null}
        <thead className={`ft-table__head${hideHeader ? " ft-table__sr" : ""}`}>
          <tr>
            {tableColumns.map((column) => (
              <HeaderCell key={column.key} column={column} sort={sort} onSortChange={onSortChange} />
            ))}
            {clickable ? (
              // The open column is named for a screen reader and unnamed on screen — the same
              // `headerHidden` treatment an actions column already gets.
              <th scope="col" className="ft-table__th ft-table__th--open" data-column="__open">
                <span className="ft-table__sr">{openColumnHeader}</span>
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody className="ft-table__body">
          {statePanel ? (
            <tr className="ft-table__row ft-table__row--state">
              <td className="ft-table__td ft-table__td--state" colSpan={columnCount}>
                {statePanel}
              </td>
            </tr>
          ) : null}
          {statePanel ? null : rows.map((row, index) => {
            const { className: rowClass, ...rowRest } = extraRowProps(row, index);
            const detail = renderRowDetail ? renderRowDetail(row, index) : null;
            return (
              <React.Fragment key={keyOf(rowKey, row, index)}>
              <tr
                className={["ft-table__row", clickable ? "ft-table__row--clickable" : "", rowClass || ""]
                  .filter(Boolean)
                  .join(" ")}
                data-row-key={String(keyOf(rowKey, row, index))}
                // NO role and NO tabIndex: a <tr> given `role="button"` would lie about the
                // table's structure to a screen reader, and a focusable row without a name is
                // worse than none. Keyboard access lives on the open control in the last cell;
                // this handler is the pointer-only shortcut layered on top of it, and it stands
                // down for any click that started on a control inside the row.
                onClick={
                  clickable
                    ? (event) => {
                        if (startedOnAControl(event)) return;
                        onRowClick(row, index, event);
                      }
                    : undefined
                }
                {...rowRest}
              >
                {tableColumns.map((column) => (
                  <td
                    key={column.key}
                    className={["ft-table__td", `ft-table__td--${alignOf(column)}`, column.className || ""]
                      .filter(Boolean)
                      .join(" ")}
                    data-column={column.key}
                  >
                    {renderCell(column, row, meta)}
                  </td>
                ))}
                {clickable ? (
                  <td className="ft-table__td ft-table__td--open" data-column="__open">
                    <RowOpenControl
                      className="ft-table__row-open"
                      label={labelOf(rowActionLabel, row, index)}
                      onActivate={(event) => onRowClick(row, index, event)}
                    />
                  </td>
                ) : null}
              </tr>

              {/* Opt-in expansion (M36B follow-up). A full-width row directly
                  beneath its own row, so a detail panel sits with the record it
                  belongs to instead of after the whole group. `renderRowDetail`
                  returning null — the default for every existing caller — emits
                  nothing at all, so no other table's markup changes. */}
              {detail ? (
                <tr className="ft-table__row ft-table__row--detail">
                  <td className="ft-table__td ft-table__td--detail" colSpan={columnCount}>
                    {detail}
                  </td>
                </tr>
              ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div ref={ref} className={classes} data-presentation={meta.presentation} {...rest}>
      {/* The card presentation has no <caption> element to carry the table's name, so the same
          string becomes the label the list points at. */}
      {!asTable && caption ? (
        <p className={captionVisible ? "ft-table__caption" : "ft-table__sr"} id={captionId}>
          {caption}
        </p>
      ) : null}

      {/* In the table presentation the state panel lives INSIDE the table, in a full-width row,
          so the column header stays. In the card presentation there is no header to keep. */}
      {asTable ? tableView() : statePanel || cardList()}

      {footer ? <div className="ft-table__footer">{footer}</div> : null}
    </div>
  );
});

export default DataTable;
export { CARD_ROLES as DATA_TABLE_CARD_ROLES };
